/**
 * Chip Group Component
 *
 * A group of selectable chips/pills for filtering or multi-selection.
 * Supports both single and multi-select modes.
 */

import { cn } from '../../lib/utils';

export interface ChipOption<T extends string = string> {
  value: T;
  label: string;
  /** Optional color dot to display */
  color?: string;
  /** Optional icon */
  icon?: React.ElementType;
}

export interface ChipGroupProps<T extends string = string> {
  /** Available options */
  options: ChipOption<T>[];
  /** Currently selected values */
  value: T[];
  /** Called when selection changes */
  onChange: (value: T[]) => void;
  /** Optional label displayed before the chips */
  label?: string;
  /** Allow multiple selection (default: true) */
  multiple?: boolean;
  /** Additional class names */
  className?: string;
  /** Treat empty selection as "all selected" (for filter UIs) */
  emptyMeansAll?: boolean;
}

export function ChipGroup<T extends string = string>({
  options,
  value,
  onChange,
  label,
  multiple = true,
  className,
  emptyMeansAll = false,
}: ChipGroupProps<T>) {
  const handleToggle = (optionValue: T) => {
    if (multiple) {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    } else {
      onChange([optionValue]);
    }
  };

  // Determine if an option should appear selected
  const isSelected = (optionValue: T): boolean => {
    if (emptyMeansAll && (value.length === 0 || value.length === options.length)) {
      return true;
    }
    return value.includes(optionValue);
  };

  return (
    // `flex-wrap` aux deux niveaux : sur un ecran etroit, les puces passent a
    // la ligne au lieu de deborder. Sans effet quand tout tient sur une ligne
    // (`gap-y-*` ne joue qu'entre deux lignes).
    <div className={cn('flex flex-wrap items-center gap-3 gap-y-2', className)}>
      {label && <span className="text-sm text-neutral-500">{label}</span>}
      <div className="flex flex-wrap items-center gap-2">
        {options.map((option) => {
          const selected = isSelected(option.value);
          const Icon = option.icon;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-colors',
                selected
                  ? 'bg-neutral-100 text-dark-500'
                  : 'bg-white text-neutral-400 hover:bg-neutral-50'
              )}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {option.color && (
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: option.color }}
                />
              )}
              <span className="text-sm font-medium">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
